/**
 * Layout for every signed-in page: a top bar with the school name and the
 * person's menu, and navigation that shows only what their role can use.
 */

import { useState } from "react";
import { NavLink, Outlet } from "react-router-dom";
import { useMe } from "../auth/MeProvider";
import { useSession } from "../auth/SessionProvider";
import { Logo } from "./Logo";

interface NavItem {
  to: string;
  label: string;
}

export function AppShell() {
  const { me, isDirector } = useMe();
  const { signOut, config } = useSession();
  const [open, setOpen] = useState(false);

  const items: NavItem[] = [];
  if (me.org) {
    items.push({ to: "/", label: "Today" });
    if (me.role?.isStaff) items.push({ to: "/time", label: "Time clock" });
    if (isDirector) items.push({ to: "/time/review", label: "Time review" });
    items.push({ to: "/help", label: "Help and feedback" });
    if (isDirector) items.push({ to: "/settings/support-access", label: "Support access" });
  }
  if (me.platformRole) items.push({ to: "/ops", label: "Operations" });

  const linkClass = ({ isActive }: { isActive: boolean }) =>
    `block rounded-xl px-3 py-2 text-sm font-medium transition-colors ${
      isActive ? "bg-leaf-50 text-leaf-800" : "text-stone-600 hover:bg-stone-100 hover:text-ink"
    }`;

  return (
    <div className="min-h-screen bg-cream text-ink">
      <header className="border-b border-stone-200 bg-white">
        <div className="mx-auto flex h-16 max-w-6xl items-center justify-between gap-4 px-4 sm:px-6">
          <div className="flex min-w-0 items-center gap-4">
            <Logo />
            {me.org ? <span className="hidden truncate text-sm text-stone-500 sm:inline">{me.org.name}</span> : null}
          </div>
          <div className="flex items-center gap-2">
            {config.environment !== "production" ? (
              <span className="hidden rounded-lg bg-amber-50 px-2 py-1 text-xs font-medium text-amber-800 sm:inline">
                {config.environment}
              </span>
            ) : null}
            <span className="hidden text-sm text-stone-600 md:inline">{me.user.fullName ?? me.user.email}</span>
            <button
              type="button"
              onClick={() => void signOut()}
              className="rounded-xl border border-stone-300 px-3 py-1.5 text-sm text-stone-700 hover:bg-stone-100"
            >
              Sign out
            </button>
            <button
              type="button"
              aria-label="Menu"
              aria-expanded={open}
              onClick={() => setOpen((v) => !v)}
              className="rounded-xl border border-stone-300 px-3 py-1.5 text-sm text-stone-700 hover:bg-stone-100 lg:hidden"
            >
              Menu
            </button>
          </div>
        </div>
      </header>

      <div className="mx-auto flex max-w-6xl gap-8 px-4 py-6 sm:px-6 lg:py-10">
        <nav className={`${open ? "block" : "hidden"} fixed inset-x-4 top-20 z-20 rounded-2xl border border-stone-200 bg-white p-3 shadow-lg lg:static lg:block lg:w-52 lg:shrink-0 lg:border-0 lg:bg-transparent lg:p-0 lg:shadow-none`}>
          <ul className="space-y-1" onClick={() => setOpen(false)}>
            {items.map((item) => (
              <li key={item.to}>
                <NavLink to={item.to} end className={linkClass}>
                  {item.label}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>
        <main className="min-w-0 flex-1">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
